import { useMemo, useRef, useState, useCallback } from 'react';
import { cn } from '@/lib/utils';
import { ItemCategory } from '@/types/item';
import { useItemCategories } from '@/hooks/useItemCategories';
import { logger } from '@/lib/logger';
import { toDateKey } from '@/utils/dateTZ';
import { useAppSettings } from '@/hooks/useAppSettings';
import { hexToRgba, resolveHighlightStyle, HIGHLIGHT_BAR_STYLE_KEY, HIGHLIGHT_BAR_STYLE_DEFAULTS } from '@/utils/highlightStyle';
import { useCreateMilestone } from '@/hooks/milestones/useItemMilestones';
import { useMilestoneTypes } from '@/hooks/milestones/useMilestoneTypes';
import { useQueryClient } from '@tanstack/react-query';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Plus } from 'lucide-react';

// Helper function to get local date string
const ymd = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Pure date-only add: 'YYYY-MM-DD' + n days → 'YYYY-MM-DD' (no timezone)
const addDaysToDateKey = (dateKey: string, days: number): string => {
  const [y, m, d] = dateKey.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + days);
  const yy = dt.getUTCFullYear();
  const mm = String(dt.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(dt.getUTCDate()).padStart(2, '0');
  return `${yy}-${mm}-${dd}`;
};

const EDGE = 6;

type DragMode = 'move' | 'resize-start' | 'resize-end';

interface TimelineBarProps {
  startDate: string;
  endDate: string;
  dateColumns: Date[];
  dayWidth?: number;
  className?: string;
  category?: ItemCategory;
  itemId?: string;
  projectId?: string;
  sortOrder?: number;
  canDrag?: boolean;
  highlighted?: boolean;
  onCommitDates?: (id: string, newStart: string, newEnd: string) => void | Promise<void>;
  onRequestDependency?: (itemId: string) => void;
  onCreateParallelItem?: (afterItemId: string, afterSortOrder: number, name: string) => Promise<void> | void;
  /** Loại mốc khi bấm "Tạo mốc" nhanh (vd nhóm mốc → event_input). Không truyền = loại đầu tiên như cũ. */
  defaultMilestoneType?: string;
}

export default function TimelineBar({ startDate, endDate, dateColumns, dayWidth = 20, className, category, itemId, projectId, sortOrder, canDrag = false, highlighted = false, onCommitDates, onRequestDependency, onCreateParallelItem, defaultMilestoneType }: TimelineBarProps) {
  const { data: categories } = useItemCategories();
  const { getSetting } = useAppSettings();
  const highlightStyle = resolveHighlightStyle(getSetting(HIGHLIGHT_BAR_STYLE_KEY, HIGHLIGHT_BAR_STYLE_DEFAULTS));
  const createMilestone = useCreateMilestone();
  const { data: milestoneTypes = [] } = useMilestoneTypes();
  const queryClient = useQueryClient();
  const [isHovered, setIsHovered] = useState(false);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [nameInput, setNameInput] = useState('');
  const [isCreatingParallel, setIsCreatingParallel] = useState(false);
  const hoverTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const setHoveredStable = useCallback((v: boolean) => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    if (v) setIsHovered(true);
    else hoverTimeoutRef.current = setTimeout(() => setIsHovered(false), 300);
  }, []);

  const categoryColorMap = useMemo(() => {
    if (!categories) return {};
    return categories.reduce((acc, cat) => {
      acc[cat.name] = cat.color;
      return acc;
    }, {} as Record<string, string>);
  }, [categories]);

  const getCategoryColor = (category?: ItemCategory): string => {
    if (!category) return '#64748b';
    return categoryColorMap[category] || '#64748b';
  };

  const barPosition = useMemo(() => {
    const startDateKey = toDateKey(startDate);
    const endDateKey = toDateKey(endDate);

    if (endDateKey < startDateKey) {
      logger.error('TimelineBar: Invalid date range', { startDate, endDate, startDateKey, endDateKey });
    }

    let startIndex = dateColumns.findIndex(date => ymd(date) === startDateKey);
    let endIndex = dateColumns.findIndex(date => ymd(date) === endDateKey);

    if (startIndex === -1) {
      if (startDateKey < ymd(dateColumns[0])) startIndex = 0;
      else return null;
    }
    if (endIndex === -1) {
      if (endDateKey > ymd(dateColumns[dateColumns.length - 1])) endIndex = dateColumns.length - 1;
      else return null;
    }

    const left = startIndex * dayWidth;
    const duration = Math.max(1, endIndex - startIndex + 1);
    const width = duration * dayWidth;
    return { left, width };
  }, [startDate, endDate, dateColumns, dayWidth]);

  const [dragOffsetPx, setDragOffsetPx] = useState(0);
  const [resizeBox, setResizeBox] = useState<{ left: number; width: number } | null>(null);
  const dragStateRef = useRef<{
    startX: number;
    deltaDays: number;
    pointerId: number;
    mode: DragMode;
    baseLeft: number;
    baseWidth: number;
  } | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [hoverEdge, setHoverEdge] = useState<'start' | 'end' | null>(null);

  const interactive = canDrag && !!onCommitDates && !!itemId;

  const getMode = useCallback((offsetX: number, width: number): DragMode => {
    if (offsetX <= EDGE) return 'resize-start';
    if (offsetX >= width - EDGE) return 'resize-end';
    return 'move';
  }, []);

  const handlePointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (!interactive || !barPosition) return;
    if (e.button !== 0) return;
    e.stopPropagation();
    const rect = (e.currentTarget as HTMLDivElement).getBoundingClientRect();
    const offsetX = e.clientX - rect.left;
    const mode = getMode(offsetX, rect.width);
    (e.currentTarget as HTMLDivElement).setPointerCapture(e.pointerId);
    dragStateRef.current = {
      startX: e.clientX,
      deltaDays: 0,
      pointerId: e.pointerId,
      mode,
      baseLeft: barPosition.left,
      baseWidth: barPosition.width,
    };
    setIsDragging(true);
    if (mode !== 'move') {
      setResizeBox({ left: barPosition.left, width: barPosition.width });
    }
  }, [interactive, barPosition, getMode]);

  const handlePointerMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    const s = dragStateRef.current;
    if (!s) {
      // hover affordance
      if (!interactive) return;
      const rect = (e.currentTarget as HTMLDivElement).getBoundingClientRect();
      const offsetX = e.clientX - rect.left;
      const m = getMode(offsetX, rect.width);
      setHoverEdge(m === 'resize-start' ? 'start' : m === 'resize-end' ? 'end' : null);
      return;
    }
    let deltaDays = Math.round((e.clientX - s.startX) / dayWidth);
    if (s.mode === 'resize-start') {
      // clamp so width >= dayWidth
      const maxDelta = Math.floor(s.baseWidth / dayWidth) - 1;
      if (deltaDays > maxDelta) deltaDays = maxDelta;
      s.deltaDays = deltaDays;
      setResizeBox({
        left: s.baseLeft + deltaDays * dayWidth,
        width: s.baseWidth - deltaDays * dayWidth,
      });
    } else if (s.mode === 'resize-end') {
      const minDelta = -(Math.floor(s.baseWidth / dayWidth) - 1);
      if (deltaDays < minDelta) deltaDays = minDelta;
      s.deltaDays = deltaDays;
      setResizeBox({
        left: s.baseLeft,
        width: s.baseWidth + deltaDays * dayWidth,
      });
    } else {
      s.deltaDays = deltaDays;
      setDragOffsetPx(deltaDays * dayWidth);
    }
  }, [dayWidth, interactive, getMode]);

  const handlePointerLeave = useCallback(() => {
    if (!dragStateRef.current) setHoverEdge(null);
  }, []);

  const finishDrag = useCallback(async (e: React.PointerEvent<HTMLDivElement>) => {
    const s = dragStateRef.current;
    if (!s) return;
    dragStateRef.current = null;
    setIsDragging(false);
    try {
      (e.currentTarget as HTMLDivElement).releasePointerCapture(s.pointerId);
    } catch {}
    const deltaDays = s.deltaDays;
    const mode = s.mode;
    const reset = () => {
      setDragOffsetPx(0);
      setResizeBox(null);
    };
    if (deltaDays === 0 || !onCommitDates || !itemId) {
      reset();
      return;
    }
    const origStart = toDateKey(startDate);
    const origEnd = toDateKey(endDate);
    try {
      if (mode === 'move') {
        const newStart = addDaysToDateKey(origStart, deltaDays);
        const newEnd = addDaysToDateKey(origEnd, deltaDays);
        await onCommitDates(itemId, newStart, newEnd);
      } else if (mode === 'resize-start') {
        const newStart = addDaysToDateKey(origStart, deltaDays);
        await onCommitDates(itemId, newStart, origEnd);
      } else if (mode === 'resize-end') {
        const newEnd = addDaysToDateKey(origEnd, deltaDays);
        await onCommitDates(itemId, origStart, newEnd);
      }
      reset();
    } catch (err) {
      logger.error('TimelineBar: commit failed', err);
      reset();
    }
  }, [onCommitDates, itemId, startDate, endDate]);

  if (!barPosition) return null;

  const isResizing = isDragging && (resizeBox !== null);
  const renderLeft = isResizing && resizeBox ? resizeBox.left : barPosition.left;
  const renderWidth = isResizing && resizeBox ? resizeBox.width : barPosition.width;

  const cursor = !interactive
    ? 'default'
    : isDragging
      ? (dragStateRef.current?.mode === 'move' ? 'grabbing' : 'ew-resize')
      : hoverEdge
        ? 'ew-resize'
        : 'grab';

  // ── Live-date tooltip while dragging ──
  let tooltipText: string | null = null;
  let tooltipLeft = 0;
  if (isDragging && dragStateRef.current) {
    const s = dragStateRef.current;
    const origStart = toDateKey(startDate);
    const origEnd = toDateKey(endDate);
    const fmt = (dk: string) => {
      const [y, m, d] = dk.split('-');
      return `${d}/${m}/${y.slice(-2)}`;
    };
    if (s.mode === 'move') {
      const deltaDays = Math.round(dragOffsetPx / dayWidth);
      const liveStart = addDaysToDateKey(origStart, deltaDays);
      const liveEnd = addDaysToDateKey(origEnd, deltaDays);
      tooltipText = `${fmt(liveStart)} → ${fmt(liveEnd)}`;
      tooltipLeft = barPosition.left + dragOffsetPx + barPosition.width / 2;
    } else if (s.mode === 'resize-start' && resizeBox) {
      const deltaDays = Math.round((resizeBox.left - barPosition.left) / dayWidth);
      const liveStart = addDaysToDateKey(origStart, deltaDays);
      tooltipText = `${fmt(liveStart)} → ${fmt(origEnd)}`;
      tooltipLeft = resizeBox.left + 4;
    } else if (s.mode === 'resize-end' && resizeBox) {
      const deltaDays = Math.round((resizeBox.width - barPosition.width) / dayWidth);
      const liveEnd = addDaysToDateKey(origEnd, deltaDays);
      tooltipText = `${fmt(origStart)} → ${fmt(liveEnd)}`;
      tooltipLeft = barPosition.left + resizeBox.width - 4;
    }
  }

  return (
    <>
      <div
        className={cn(
          "absolute top-1/2 -translate-y-1/2 h-2 rounded-sm",
          className
        )}
        style={{
          left: `${renderLeft}px`,
          width: `${renderWidth}px`,
          backgroundColor: getCategoryColor(category),
          opacity: isDragging ? 0.5 : highlighted ? 0.15 : 0.06,
          zIndex: interactive ? 40 : 1,
          pointerEvents: interactive ? 'auto' : 'none',
          transform: dragStateRef.current?.mode === 'move' || (!isResizing && dragOffsetPx !== 0)
            ? `translate(${dragOffsetPx}px, -50%)`
            : `translateY(-50%)`,
          cursor,
          touchAction: interactive ? 'none' : undefined,
        }}
        onPointerDown={interactive ? handlePointerDown : undefined}
        onPointerMove={interactive ? handlePointerMove : undefined}
        onPointerUp={interactive ? finishDrag : undefined}
        onPointerCancel={interactive ? finishDrag : undefined}
        onPointerLeave={interactive ? handlePointerLeave : undefined}
        onMouseEnter={interactive ? () => setHoveredStable(true) : undefined}
        onMouseLeave={interactive ? () => setHoveredStable(false) : undefined}
        onDoubleClick={onRequestDependency && itemId ? (e) => {
          e.stopPropagation();
          e.preventDefault();
          onRequestDependency(itemId);
        } : undefined}
      />
      {interactive && itemId && (isHovered || popoverOpen) && (
        <Popover open={popoverOpen} onOpenChange={(o) => {
          setPopoverOpen(o);
          if (!o) setNameInput('');
        }}>
          <PopoverTrigger asChild>
            <button
              type="button"
              className="absolute flex items-center justify-center rounded-full bg-primary text-primary-foreground shadow hover:bg-primary/90"
              style={{
                top: '50%',
                left: `${barPosition.left + barPosition.width + EDGE}px`,
                width: '22px',
                height: '22px',
                transform: 'translateY(-50%)',
                zIndex: 50,
              }}
              onMouseEnter={() => setHoveredStable(true)}
              onMouseLeave={() => setHoveredStable(false)}
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => {
                e.stopPropagation();
                setPopoverOpen(true);
              }}
              aria-label="Thêm mốc"
            >
              <Plus className="h-4 w-4" />
            </button>
          </PopoverTrigger>
          <PopoverContent
            className="w-64 p-3"
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex flex-col gap-2">
              <Input
                value={nameInput}
                onChange={(e) => setNameInput(e.target.value)}
                placeholder="Tên mốc / item song song"
                autoFocus
              />
              <Button
                size="sm"
                disabled={!nameInput.trim() || !(defaultMilestoneType || milestoneTypes?.[0]?.name) || createMilestone.isPending}
                onClick={async () => {
                  const typeName = defaultMilestoneType || milestoneTypes?.[0]?.name;
                  if (!typeName || !itemId) return;
                  const endKey = toDateKey(endDate);
                  try {
                    await createMilestone.mutateAsync({
                      item_id: itemId,
                      name: nameInput.trim(),
                      milestone_type: typeName,
                      start_date: endKey,
                      end_date: endKey,
                    });
                    queryClient.invalidateQueries({ queryKey: ['items-optimized'] });
                    setPopoverOpen(false);
                    setNameInput('');
                  } catch (err) {
                    logger.error('TimelineBar: create milestone failed', err);
                  }
                }}
              >
                Tạo mốc
              </Button>
              <div className="border-t pt-2 mt-1">
                <p className="text-xs text-muted-foreground mb-2">Hoặc tạo item song song</p>
                <Button
                  size="sm"
                  variant="outline"
                  className="w-full"
                  disabled={!nameInput.trim() || !itemId || sortOrder == null || !onCreateParallelItem || isCreatingParallel}
                  onClick={async () => {
                    if (!nameInput.trim() || !itemId || sortOrder == null || !onCreateParallelItem || isCreatingParallel) return;
                    setIsCreatingParallel(true);
                    try {
                      await onCreateParallelItem(itemId, sortOrder, nameInput.trim());
                      setPopoverOpen(false);
                      setNameInput('');
                    } catch (err) {
                      logger.error('TimelineBar: create parallel item failed', err);
                    } finally {
                      setIsCreatingParallel(false);
                    }
                  }}
                >
                  Tạo item song song
                </Button>
              </div>
            </div>
          </PopoverContent>
        </Popover>
      )}
      {highlighted && (
        <div
          style={{
            position: 'absolute',
            top: '50%',
            left: `${renderLeft}px`,
            width: `${renderWidth}px`,
            height: '8px',
            outlineOffset: '2px',
            transform: dragStateRef.current?.mode === 'move' || (!isResizing && dragOffsetPx !== 0)
              ? `translate(${dragOffsetPx}px, -50%)`
              : `translateY(-50%)`,
            zIndex: 25,
            pointerEvents: 'none',
            outline: highlightStyle.border_enabled
              ? `${highlightStyle.border_width}px dashed ${highlightStyle.border_color}`
              : 'none',
            borderRadius: '2px',
            background: hexToRgba(highlightStyle.fill_color, highlightStyle.fill_opacity),
          }}
        />
      )}

      {interactive && hoverEdge && !isDragging && (
        <div
          className="absolute top-1/2 -translate-y-1/2 h-2"
          style={{
            left: `${hoverEdge === 'start' ? barPosition.left : barPosition.left + barPosition.width - 2}px`,
            width: '2px',
            backgroundColor: '#111827',
            opacity: 1,
            zIndex: 50,
            pointerEvents: 'none',
          }}
        />
      )}
      {tooltipText && (
        <div
          className="absolute px-2 py-1 rounded text-xs font-medium whitespace-nowrap"
          style={{
            top: '50%',
            left: `${tooltipLeft}px`,
            transform: 'translate(-50%, calc(-50% - 10px))',
            backgroundColor: 'rgba(0,0,0,0.6)',
            backdropFilter: 'blur(2px)',
            WebkitBackdropFilter: 'blur(2px)',
            color: '#f9fafb',
            zIndex: 50,
            pointerEvents: 'none',
            opacity: 1,
          }}
        >
          {tooltipText}
        </div>
      )}
    </>
  );
}
